import { useState, useRef, useEffect } from "react";
import { useCurrency } from "../contexts/CurrencyContext";
import { Input } from "./ui/input";

/**
 * Price input with IQD/USD toggle.
 * - value: number (always IQD, stored in base currency)
 * - onChange: (numberInIQD) => void
 */
export default function PriceInput({
  value,
  onChange,
  placeholder = "0",
  className = "",
  testId = "price-input",
  disabled = false,
  allowNegative = false,
}) {
  const { rate } = useCurrency();
  const [currency, setCurrency] = useState("iqd"); // "iqd" | "usd"
  const [displayValue, setDisplayValue] = useState("");
  const lastValueRef = useRef(null);

  useEffect(() => {
    const n = Number(value);
    if (!isFinite(n) || n === 0) {
      if (lastValueRef.current !== value) {
        setDisplayValue("");
      }
      lastValueRef.current = value;
      return;
    }
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;
    if (currency === "iqd") {
      setDisplayValue(String(n));
    } else {
      setDisplayValue((n / rate).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, rate]);

  const switchCurrency = (target) => {
    if (target === currency) return;
    const iqd = Number(value) || 0;
    if (target === "usd") {
      setDisplayValue(iqd ? (iqd / rate).toFixed(2) : "");
    } else {
      setDisplayValue(iqd ? String(iqd) : "");
    }
    setCurrency(target);
  };

  const handleInput = (e) => {
    let raw = e.target.value;
    const re = allowNegative ? /[^\d.-]/g : /[^\d.]/g;
    raw = raw.replace(re, "");
    const firstDot = raw.indexOf(".");
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, "");
    }
    setDisplayValue(raw);
    const numeric = parseFloat(raw) || 0;
    const iqdValue = currency === "usd" ? Math.round(numeric * rate) : numeric;
    lastValueRef.current = iqdValue;
    onChange(iqdValue);
  };

  let preview = "";
  const currentNum = parseFloat(displayValue) || 0;
  if (currentNum > 0) {
    if (currency === "iqd") {
      preview = `≈ $${(currentNum / rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      const iqd = currentNum * rate;
      preview = `= ${iqd.toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع`;
    }
  }

  return (
    <div className="space-y-2" dir="rtl">
      {/* Visible currency toggle ABOVE the input */}
      <div className="flex items-center gap-2" dir="ltr">
        <span className="text-[10px] text-slate-400 font-bold">العملة:</span>
        <div className="flex bg-slate-950 rounded-md border border-slate-700 overflow-hidden shadow-sm">
          <button
            type="button"
            onClick={() => switchCurrency("iqd")}
            data-testid={`${testId}-toggle-iqd`}
            className={`text-xs px-3 py-1 font-bold transition-all ${
              currency === "iqd"
                ? "bg-emerald-500 text-white shadow-inner"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            د.ع IQD
          </button>
          <button
            type="button"
            onClick={() => switchCurrency("usd")}
            data-testid={`${testId}-toggle-usd`}
            className={`text-xs px-3 py-1 font-bold transition-all border-r border-slate-700 ${
              currency === "usd"
                ? "bg-emerald-500 text-white shadow-inner"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            $ USD
          </button>
        </div>
      </div>

      <Input
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleInput}
        disabled={disabled}
        placeholder={currency === "usd" ? `$${placeholder}` : placeholder}
        dir="ltr"
        className={`text-left font-mono ${className}`}
        data-testid={testId}
      />

      {preview && (
        <p
          dir="ltr"
          className="text-[11px] text-emerald-400/80 font-mono text-left"
          data-testid={`${testId}-preview`}
        >
          {preview}
        </p>
      )}
    </div>
  );
}
